import { useState } from "react";
import styled from "styled-components";
import { FaExclamationTriangle } from "react-icons/fa";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { Image } from "../../components/Image";
import { Modal } from "../../components/Modal";
import { Box } from "../../components/Box";
import { H2, H4 } from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import { TextArea } from "../../components/TextArea";
import accept from "../../assets/manual-accept.svg";
import reject from "../../assets/manual-reject.svg";
import { useManualAcceptReject } from "../../data/useManualAcceptReject";
import { individualCheckQueryKey } from "../../data/useIndividualCheck";
import { showError } from "../../utils/error-handling";
import { queryClient } from "../../queryClient";
import { notify } from "../../utils/notify";

export type ReviewChange = {
  field: string;
  oldValue: string;
  newValue: string;
};

export type ReviewChangeGroup = {
  title: string;
  changes: ReviewChange[];
};

type ReviewAction = "accept" | "reject";

type Props = {
  title: string;
  id: number;
  changes: ReviewChange[];
  changeGroups?: ReviewChangeGroup[];
  onSuccess?: () => void;
};

const WarningBox = styled(Flex)<{ action: ReviewAction }>`
  ${({ action }) => `
    background: ${action === "accept" ? "#FFE5D8" : "#F1F4F9"};
    border-radius: 8px;
    padding: 12px;
  `}
`;

const ChangesTable = styled.table`
  ${({ theme: { colors, fontFamily } }) => `
    width: 100%;
    border-collapse: collapse;
    font-family: ${fontFamily};

    th {
      color: ${colors.gray[50]};
      font-size: 11px;
      font-weight: 500;
      padding: 8px 0;
      text-align: left;
    }

    td {
      color: ${colors.blue[100]};
      font-size: 12px;
      padding: 6px 0;
      vertical-align: top;
    }
  `}
`;

const ChangeGroupTitle = styled(H4)`
  ${({ theme: { space } }) => `
    margin-top: ${space[2]};
    margin-bottom: ${space[0]};
  `}
`;

const ModalBody = styled(Box)`
  max-height: calc(95vh - 60px);
  overflow-y: auto;
  padding-right: 4px;
`;

const ChangesTableWrap = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const Divider = styled.hr`
  ${({ theme: { colors } }) => `
    border: 0;
    border-top: 1px solid ${colors.gray[20]};
    margin: 14px 0;
  `}
`;

export const ChangesReviewManualAcceptReject = (props: Props) => {
  const { title, id, changes, changeGroups, onSuccess } = props;
  const [action, setAction] = useState<ReviewAction | null>(null);
  const [comment, setComment] = useState("");
  const [hasCommentError, setHasCommentError] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const { mutate: manualAcceptReject } = useManualAcceptReject();

  const isAccept = action === "accept";
  const modalTitle = `Are you sure you want to ${
    isAccept ? "accept" : "reject"
  } ${title}?`;
  const commentLabel = isAccept
    ? "Please leave a comment to confirm why you are accepting this check"
    : "Please leave a rejection reason";
  const confirmText = isAccept ? "Accept" : "Reject";

  const closeModal = () => {
    if (isLoadingAction) return;
    setAction(null);
    setComment("");
    setHasCommentError(false);
  };

  const handleCommentChange = (event: any) => {
    setComment(event.target.value);
    setHasCommentError(false);
  };

  const handleConfirm = () => {
    if (!action) return;

    if (!comment.trim()) {
      setHasCommentError(true);
      return;
    }

    setIsLoadingAction(true);
    manualAcceptReject(
      {
        id,
        action,
        comment,
        comment_for: "IndividualCheck",
        isMonitoring: false,
      },
      {
        onSuccess: () => {
          setIsLoadingAction(false);
          setAction(null);
          setComment("");
          setHasCommentError(false);
          queryClient.invalidateQueries(individualCheckQueryKey(id));
          queryClient.invalidateQueries(["individual"]);
          notify(`${title} ${action === "accept" ? "accepted" : "rejected"}.`, {
            type: "success",
            position: "bottom-left",
          });
          onSuccess?.();
        },
        onError: (err: any) => {
          setIsLoadingAction(false);
          showError(
            err,
            `A problem occurred while manually ${
              action === "accept" ? "accepting" : "rejecting"
            } check.`,
          );
        },
      },
    );
  };

  return (
    <>
      <Flex mr={5}>
        <Button
          px={5}
          variant="secondary"
          color="red.primary"
          mr={2}
          fontWeight="500"
          onClick={() => setAction("reject")}
        >
          <Image src={reject} alt="reject" height="20px" width="auto" mr={1} />
          Reject
        </Button>
        <Button
          px={5}
          mr={4}
          variant="secondary"
          fontWeight="500"
          color="blue.primary"
          onClick={() => setAction("accept")}
        >
          <Image src={accept} alt="accept" height="20px" width="auto" mr={1} />
          Accept
        </Button>
      </Flex>

      <Modal
        isClosingDisabled={isLoadingAction}
        isOpen={!!action}
        onClose={closeModal}
        width="450px"
      >
        <ModalBody>
          <H2 mr={3} mb={3}>
            {modalTitle}
          </H2>

          <WarningBox action={action || "accept"} alignItems="center" mb={3}>
            <Box color={isAccept ? "#FF8A4C" : "gray.60"} mr={2}>
              <FaExclamationTriangle size={22} />
            </Box>
            <Box>
              <H4 color={isAccept ? "#FF8A4C" : "gray.60"} fontSize={1} mb={0}>
                {isAccept ? "Warning!" : "Information"}
              </H4>
              <Paragraph
                color={isAccept ? "#FF8A4C" : "gray.60"}
                fontSize={1}
                my={0}
              >
                {isAccept
                  ? "Once accepted, these changes cannot be reversed."
                  : "Profile information will not be updated."}
              </Paragraph>
            </Box>
          </WarningBox>

          <Paragraph fontSize={1} my={0} mb={1}>
            {isAccept
              ? "Profile information that will be updated:"
              : "Profile information changes being rejected:"}
          </Paragraph>

          <ChangesTableWrap>
            {changeGroups?.length ? (
              changeGroups.map((group) => (
                <Box key={group.title}>
                  <ChangeGroupTitle>{group.title}</ChangeGroupTitle>
                  <ChangesTable>
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Old</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.changes.map((change) => (
                        <tr key={`${group.title}-${change.field}`}>
                          <td>{change.field}</td>
                          <td>{change.oldValue}</td>
                          <td>{change.newValue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ChangesTable>
                </Box>
              ))
            ) : (
              <ChangesTable>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Old</th>
                    <th>New</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.length > 0 ? (
                    changes.map((change) => (
                      <tr key={change.field}>
                        <td>{change.field}</td>
                        <td>{change.oldValue}</td>
                        <td>{change.newValue}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3}>No profile changes detected.</td>
                    </tr>
                  )}
                </tbody>
              </ChangesTable>
            )}
          </ChangesTableWrap>

          <Divider />

          <TextArea
            label={commentLabel}
            value={comment}
            onChange={handleCommentChange}
            placeholder="Enter comment"
            rows={3}
            hasError={hasCommentError}
            errorMessage={
              hasCommentError
                ? isAccept
                  ? "Comment is required"
                  : "Rejection reason is required"
                : undefined
            }
          />

          <Flex justifyContent="end" mt={3}>
            <Button
              type="button"
              isDisabled={isLoadingAction}
              onClick={closeModal}
              variant="text"
              color="gray.50"
              mr={2}
            >
              Back
            </Button>
            <Button
              type="button"
              isDisabled={isLoadingAction || !comment.trim()}
              onClick={handleConfirm}
              variant={isAccept ? "primary" : "danger"}
            >
              {isLoadingAction
                ? isAccept
                  ? "Accepting..."
                  : "Rejecting..."
                : confirmText}
            </Button>
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
};
